import {
  Box,
  Button,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { Download, Eye, Info, MousePointerClick } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { ShelfMetrics } from '../../../services/shelf-service';

interface ShelfInsightsTableProps {
  metrics: ShelfMetrics[];
}

type Order = 'asc' | 'desc';
type ShelfTopItem = NonNullable<ShelfMetrics['top_items']>[number];

interface Column {
  id: 'shown' | 'click' | 'clickr';
  label: string;
  subLabel: string;
  icon?: React.ElementType;
  iconColor?: string;
  format: (value: number) => string;
  group: '見た' | '使った';
}

const columns: Column[] = [
  {
    id: 'shown',
    label: '見た',
    subLabel: 'IMP数',
    icon: Eye,
    iconColor: '#2196F3',
    format: (value: number) => value.toLocaleString(),
    group: '見た',
  },
  {
    id: 'click',
    label: '使った',
    subLabel: 'クリック数',
    icon: MousePointerClick,
    iconColor: '#4CAF50',
    format: (value: number) => value.toLocaleString(),
    group: '使った',
  },
  {
    id: 'clickr',
    label: '使った',
    subLabel: 'クリック率',
    format: (value: number) => `${(value * 100).toFixed(3)}%`,
    group: '使った',
  },
];

const ALL_PAGE_TYPE = 'all';

const pageTypeLabels: Record<string, string> = {
  [ALL_PAGE_TYPE]: '全体',
  top: 'トップ',
  pdp: '商品詳細',
  list: '商品一覧',
  category_search: 'カテゴリ検索',
  brand: 'ブランド',
  cart_show: 'カート',
  order_complete: '注文完了',
};

const desiredPageTypeOrder = [
  ALL_PAGE_TYPE,
  'top',
  'pdp',
  'list',
  'category_search',
  'brand',
  'cart_show',
  'order_complete',
];

const isKodawariShelf = (metric: ShelfMetrics): boolean =>
  metric.shelf_type === 'IXニーズ' || metric.shelf_type === 'IX検索キーワード';

const getShelfKey = (metric: ShelfMetrics): string =>
  `${metric.shelf_type}-${metric.gray_tagline}-${metric.bold_tagline}`;

const normalizePageType = (pageType: string): string => {
  if (
    pageType === 'top' ||
    pageType === 'ttop' ||
    pageType.startsWith('top_')
  ) {
    return 'top';
  }

  if (pageType === 'shop(incl.brand)') {
    return 'brand';
  }

  return pageType;
};

const getPageTypeLabel = (pageType: string): string =>
  pageTypeLabels[pageType] ?? pageType;

const getPageTypeOrder = (pageType: string): number => {
  const index = desiredPageTypeOrder.indexOf(normalizePageType(pageType));
  return index === -1 ? desiredPageTypeOrder.length : index;
};

const sortPageTypes = (pageTypes: string[]): string[] =>
  [...pageTypes].sort((a, b) => {
    const orderA = getPageTypeOrder(a);
    const orderB = getPageTypeOrder(b);

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    return getPageTypeLabel(a).localeCompare(getPageTypeLabel(b), 'ja');
  });

const calculateClickRate = (metric: ShelfMetrics): number => {
  if (metric.shown === 0) {
    return 0;
  }

  return metric.click / metric.shown;
};

const mergeTopItems = (
  currentItems: ShelfTopItem[] | undefined,
  nextItems: ShelfTopItem[] | undefined,
): ShelfTopItem[] | undefined => {
  if (!currentItems && !nextItems) {
    return undefined;
  }

  const topItemsByAid = new Map<string, ShelfTopItem>();

  [...(currentItems ?? []), ...(nextItems ?? [])].forEach((item) => {
    const existing = topItemsByAid.get(item.aid);
    if (!existing) {
      topItemsByAid.set(item.aid, { ...item });
      return;
    }

    topItemsByAid.set(item.aid, {
      ...existing,
      item_shown: existing.item_shown + item.item_shown,
    });
  });

  return Array.from(topItemsByAid.values()).sort(
    (a, b) => b.item_shown - a.item_shown,
  );
};

const aggregateMetrics = (
  metrics: ShelfMetrics[],
  selectedPageType: string,
): ShelfMetrics[] => {
  const metricsByShelf = new Map<string, ShelfMetrics>();

  metrics
    .filter(isKodawariShelf)
    .filter(
      (metric) =>
        selectedPageType === ALL_PAGE_TYPE ||
        normalizePageType(metric.url_page_type) === selectedPageType,
    )
    .forEach((metric) => {
      const key = getShelfKey(metric);
      const existing = metricsByShelf.get(key);

      if (!existing) {
        metricsByShelf.set(key, {
          ...metric,
          url_page_type: selectedPageType,
          top_items: mergeTopItems(undefined, metric.top_items),
        });
        return;
      }

      const shown = existing.shown + metric.shown;
      const click = existing.click + metric.click;
      const scroll = existing.scroll + metric.scroll;
      const cv = existing.cv + metric.cv;
      const cart = existing.cart + metric.cart;
      const favorite = existing.favorite + metric.favorite;

      metricsByShelf.set(key, {
        ...existing,
        shown,
        click,
        scroll,
        cv,
        cart,
        favorite,
        scrollr: shown > 0 ? (scroll / shown) * 100 : 0,
        clickr: shown > 0 ? (click / shown) * 100 : 0,
        cvr: click > 0 ? (cv / click) * 100 : 0,
        cartr: click > 0 ? (cart / click) * 100 : 0,
        favoriter: click > 0 ? (favorite / click) * 100 : 0,
        top_items: mergeTopItems(existing.top_items, metric.top_items),
      });
    });

  return Array.from(metricsByShelf.values());
};

const getMetricValue = (
  metric: ShelfMetrics,
  columnId: 'shown' | 'click' | 'clickr',
): number => {
  if (columnId === 'clickr') {
    return calculateClickRate(metric);
  }

  return metric[columnId];
};

const escapeCSVValue = (value: string | number): string => {
  const stringValue = String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const ShelfInsightsTable: React.FC<ShelfInsightsTableProps> = ({ metrics }) => {
  const [orderBy, setOrderBy] = useState<'shown' | 'click' | 'clickr'>('shown');
  const [order, setOrder] = useState<Order>('desc');
  const [selectedPageType, setSelectedPageType] =
    useState<string>(ALL_PAGE_TYPE);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [validImageCounts, setValidImageCounts] = useState<
    Record<string, number>
  >({});

  const generateCSV = (data: ShelfMetrics[]) => {
    const headers = [
      '掲載ページ',
      'シェルフ名',
      'IMP数',
      'クリック数',
      'クリック率(%)',
    ];

    const csvRows = [
      headers.join(','),
      ...data.map((metric) => {
        const clickRate = calculateClickRate(metric) * 100;
        return [
          escapeCSVValue(getPageTypeLabel(metric.url_page_type)),
          escapeCSVValue(metric.bold_tagline),
          metric.shown.toString(),
          metric.click.toString(),
          clickRate.toFixed(3),
        ].join(',');
      }),
    ];

    return csvRows.join('\n');
  };

  const downloadCSV = () => {
    const csvContent = generateCSV(sortedMetrics);
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:-]/g, '');
    const selectedPageLabel = getPageTypeLabel(selectedPageType);
    const filename = `こだわりシェルフパフォーマンス_${selectedPageLabel}_${timestamp}.csv`;

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleRequestSort = (property: 'shown' | 'click' | 'clickr') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pageTypes = React.useMemo(() => {
    const metricPageTypes = Array.from(
      new Set(
        metrics
          .filter(isKodawariShelf)
          .map((metric) => normalizePageType(metric.url_page_type)),
      ),
    );

    return sortPageTypes([ALL_PAGE_TYPE, ...metricPageTypes]);
  }, [metrics]);

  useEffect(() => {
    if (!pageTypes.includes(selectedPageType)) {
      setSelectedPageType(pageTypes[0]);
    }
  }, [pageTypes, selectedPageType]);

  useEffect(() => {
    setPage(0);
  }, [metrics, order, orderBy, selectedPageType]);

  const { sortedMetrics, maxValues } = React.useMemo(() => {
    const aggregatedMetrics = aggregateMetrics(metrics, selectedPageType);

    const calculatedMaxValues = columns.reduce(
      (acc, column) => {
        const values = aggregatedMetrics.map((metric) =>
          getMetricValue(metric, column.id),
        );
        acc[column.id] = Math.max(0, ...values);
        return acc;
      },
      {} as Record<string, number>,
    );

    const sorted = [...aggregatedMetrics].sort((a, b) => {
      const comparison =
        getMetricValue(a, orderBy) - getMetricValue(b, orderBy);
      return order === 'desc' ? -comparison : comparison;
    });

    return { sortedMetrics: sorted, maxValues: calculatedMaxValues };
  }, [metrics, order, orderBy, selectedPageType]);

  const paginatedMetrics = sortedMetrics.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const renderMetricsBar = (
    value: number,
    maxValue: number,
    columnId: 'shown' | 'click' | 'clickr',
  ) => {
    const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
    const limitedPercentage = Math.min(percentage, 99);

    const getBarColor = () => {
      switch (columnId) {
        case 'shown':
          return 'rgba(33, 150, 243, 0.15)'; // Eye color
        case 'click':
        case 'clickr':
          return 'rgba(76, 175, 80, 0.15)'; // MousePointerClick color
        default:
          return 'grey.200';
      }
    };

    return (
      <Box
        className="metrics-bar"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: `${limitedPercentage}%`,
          bgcolor: getBarColor(),
          opacity: 0.8,
          transition: 'opacity 0.2s',
        }}
      />
    );
  };

  if (!metrics || metrics.length === 0) {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell colSpan={4} align="center">
                データがありません
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '1.2rem',
                fontWeight: 'bold',
              }}
            >
              📊 こだわりシェルフのパフォーマンス
              <Tooltip
                title="集計期間中に10回以上ユーザに表示されたシェルフの統計データ"
                placement="right"
                arrow
              >
                <Info
                  size={16}
                  style={{
                    color: '#666',
                    cursor: 'help',
                    opacity: 0.7,
                  }}
                />
              </Tooltip>
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download size={16} />}
              onClick={downloadCSV}
              sx={{
                ml: 2,
                color: 'primary.main',
                borderColor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              CSV ダウンロード
            </Button>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={selectedPageType}
              onChange={(_, value) => setSelectedPageType(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 40,
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                },
              }}
            >
              {pageTypes.map((pageType) => (
                <Tab
                  key={pageType}
                  value={pageType}
                  label={getPageTypeLabel(pageType)}
                  sx={{
                    minHeight: 40,
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    '&.Mui-selected': {
                      color: '#1976d2',
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>
          <TableContainer
            sx={{
              maxHeight: 1380,
              borderRadius: 2,
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      minWidth: 200,
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      bgcolor: 'background.paper',
                      borderRight: '1px solid',
                      borderRightColor: 'divider',
                    }}
                    rowSpan={2}
                  >
                    シェルフ名
                  </TableCell>
                  <TableCell
                    align="center"
                    colSpan={1}
                    sx={{
                      borderBottom: 'none',
                      height: '40px',
                      verticalAlign: 'bottom',
                      pb: 1,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: '-1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 0,
                        height: 0,
                        borderTop: '10px solid transparent',
                        borderBottom: '10px solid transparent',
                        borderLeft: '10px solid rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <Eye size={16} style={{ color: '#2196F3' }} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: 'text.primary',
                          }}
                        >
                          見た
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                        }}
                      >
                        インプレッション
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    colSpan={2}
                    sx={{
                      borderBottom: 'none',
                      height: '40px',
                      verticalAlign: 'bottom',
                      pb: 1,
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: '-1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 0,
                        height: 0,
                        borderTop: '10px solid transparent',
                        borderBottom: '10px solid transparent',
                        borderLeft: '10px solid rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <MousePointerClick
                          size={16}
                          style={{ color: '#4CAF50' }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            color: 'text.primary',
                          }}
                        >
                          使った
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                        }}
                      >
                        魅力度
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      minWidth: 300,
                      borderRight: '1px solid',
                      borderRightColor: 'divider',
                      textAlign: 'center',
                    }}
                    rowSpan={2}
                  >
                    シェルフ内の商品例
                  </TableCell>
                </TableRow>
                <TableRow>
                  {columns.map((column) => {
                    const getTooltipContent = (columnId: string) => {
                      switch (columnId) {
                        case 'shown':
                          return (
                            <Typography variant="body2">
                              集計期間内にシェルフがユーザの画面に表示された回数
                            </Typography>
                          );
                        case 'click':
                          return (
                            <Typography variant="body2">
                              集計期間内にシェルフがユーザにクリックされた回数
                            </Typography>
                          );
                        case 'clickr':
                          return (
                            <Typography variant="body2">
                              集計期間内においてシェルフが表示された際にクリックされた割合
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-block',
                                }}
                              >
                                （クリック数 / IMP数）
                              </Box>
                            </Typography>
                          );
                        default:
                          return '';
                      }
                    };

                    return (
                      <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          textAlign: 'center',
                          borderBottom: '2px solid',
                          borderBottomColor: 'divider',
                          borderRight: '1px solid',
                          borderRightColor: 'divider',
                          color: 'text.secondary',
                          height: '60px',
                          verticalAlign: 'top',
                          pt: 1,
                          bgcolor: 'background.paper',
                          minWidth: column.id === 'shown' ? '140px' : 'auto',
                          width: column.id === 'shown' ? '140px' : 'auto',
                          '&:last-child': {
                            borderRight: 'none',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          {/* 上の行：カラム名 */}
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 'bold',
                              textAlign: 'center',
                            }}
                          >
                            {column.subLabel}
                          </Typography>

                          {/* 下の行：ツールチップとソートボタン */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Tooltip
                              title={getTooltipContent(column.id)}
                              placement="top"
                              arrow
                            >
                              <Info
                                size={12}
                                style={{
                                  color: '#999',
                                  opacity: 0.6,
                                  cursor: 'help',
                                }}
                              />
                            </Tooltip>

                            <TableSortLabel
                              active={orderBy === column.id}
                              direction={orderBy === column.id ? order : 'asc'}
                              onClick={() => handleRequestSort(column.id)}
                              sx={{
                                '& .MuiTableSortLabel-icon': {
                                  fontSize: '16px',
                                  opacity: 1,
                                },
                                '&:hover': {
                                  color: 'primary.main',
                                },
                                minWidth: 'auto',
                                padding: '4px',
                              }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedMetrics.map((metric) => (
                  <TableRow
                    key={`${metric.url_page_type}-${getShelfKey(metric)}`}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                        '& .metrics-bar': {
                          opacity: 0.7,
                        },
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        bgcolor: 'background.paper',
                        zIndex: 2,
                        borderRight: '1px solid',
                        borderRightColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {metric.bold_tagline}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                      >
                        {metric.gray_tagline}
                      </Typography>
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          position: 'relative',
                          borderRight: '1px solid',
                          borderRightColor: 'divider',
                          minWidth: column.id === 'shown' ? '140px' : 'auto',
                          width: column.id === 'shown' ? '140px' : 'auto',
                          '&:last-child': {
                            borderRight: 'none',
                          },
                        }}
                      >
                        {renderMetricsBar(
                          getMetricValue(metric, column.id),
                          maxValues[column.id],
                          column.id,
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: 'center',
                            position: 'relative',
                            zIndex: 1,
                            fontWeight: 'bold',
                          }}
                        >
                          {column.id === 'clickr'
                            ? column.format(calculateClickRate(metric))
                            : column.format(metric[column.id])}
                        </Typography>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        borderRight: '1px solid',
                        borderRightColor: 'divider',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          overflowX: 'auto',
                          '&::-webkit-scrollbar': {
                            height: '6px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            borderRadius: '3px',
                          },
                        }}
                      >
                        {metric.top_items
                          ?.slice(0, 30)
                          .filter((item) => item.item_title && item.brand_name)
                          .map((item) => {
                            const key = `${metric.url_page_type}-${getShelfKey(metric)}-${item.aid}`;
                            const shelfKey = `${metric.url_page_type}-${getShelfKey(metric)}`;
                            const validCount = validImageCounts[shelfKey] || 0;

                            const shouldDisplay =
                              loadedImages[key] === true ||
                              (loadedImages[key] === undefined &&
                                validCount < 10);

                            if (!shouldDisplay) return null;

                            return (
                              <Box
                                key={key}
                                component="a"
                                href={item.item_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  textDecoration: 'none',
                                  color: 'inherit',
                                  minWidth: '60px',
                                  maxWidth: '60px',
                                  display:
                                    loadedImages[key] === false
                                      ? 'none'
                                      : 'block',
                                }}
                              >
                                <img
                                  src={item.image_url}
                                  alt={item.item_title}
                                  loading="lazy"
                                  onError={() => {
                                    setLoadedImages((prev) => ({
                                      ...prev,
                                      [key]: false,
                                    }));
                                  }}
                                  onLoad={() => {
                                    setLoadedImages((prev) => ({
                                      ...prev,
                                      [key]: true,
                                    }));
                                    setValidImageCounts((prev) => ({
                                      ...prev,
                                      [shelfKey]: (prev[shelfKey] || 0) + 1,
                                    }));
                                  }}
                                  style={{
                                    width: '100%',
                                    height: '80px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    mt: 0.5,
                                    fontSize: '0.65rem',
                                    color: 'text.secondary',
                                    lineHeight: 1.2,
                                    height: '1.6rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  {item.brand_name}
                                </Typography>
                              </Box>
                            );
                          })
                          .filter(Boolean)
                          .slice(0, 10)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={sortedMetrics.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="表示件数"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count}`
            }
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ShelfInsightsTable;
